import { useContext, useEffect, useState } from "react";

import { useCurrentPlanStateQuery } from "@/apollo/types";
import { useCurrentUser } from "@/providers/UserProvider";
import { AccountContext } from "@/providers/account";
import * as Sentry from "@sentry/react";

import {
  FULL_MESSENGER_LOAD_DELAY_MS,
  loadFullMessenger,
  whenIdle,
} from "./full-messenger-preload";
import { syncLauncherVisibility } from "./launcher-visibility";
import { identify } from "./messenger-identity";
import { watchPassiveNotifications } from "./passive-notifications";
import { useStartIntercomChecklistForNewAccounts } from "./onboarding-checklist";
import { buildIntercomAttributes } from "./user-attributes";

/**
 * Keeps Intercom in step with who is signed in and which workspace they are
 * looking at. Mounted once, inside a workspace.
 */
export function useUserIntercom() {
  const user = useCurrentUser();
  const currentAccount = useContext(AccountContext)?.account;

  const planQuery = useCurrentPlanStateQuery();
  const planName = planQuery.data?.currentPlanState.plan.name;

  // Who Intercom is currently identified for, rather than merely whether it
  // is. A boolean only ever goes false -> true, so a change of user would
  // leave the preload effect below dormant — identify() resets the preload for
  // the new session, but nothing would run it, stranding them in lightweight
  // mode with unread counts stuck at 0.
  const [identifiedFor, setIdentifiedFor] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Runs on every change to the user, workspace or plan. Only the first one
    // reaches Intercom's boot; the rest are `update` calls that identify()
    // skips entirely when the attributes have not actually changed.
    identify(buildIntercomAttributes(user, currentAccount, planName))
      .then(() => {
        if (!cancelled) setIdentifiedFor(user.id);
      })
      .catch((error) => {
        Sentry.captureException(error, {
          tags: {
            feature: "intercom-initialization",
          },
        });
      });

    return () => {
      cancelled = true;
    };
  }, [currentAccount, planName, user]);

  // Deferred rather than run alongside identify(): it pulls the full messenger
  // down, which nothing on screen is waiting for. A conversation Intercom is
  // restoring is protected by the DOM check in openAndClose, not by waiting.
  //
  // Re-identifying the same user sets the same id, so React bails out and this
  // does not run again — which is what we want; only a real switch should.
  useEffect(() => {
    if (identifiedFor === null) return;
    return whenIdle(loadFullMessenger, FULL_MESSENGER_LOAD_DELAY_MS);
  }, [identifiedFor]);

  // Ticket updates never reach the unread count and have no callback, so the
  // launcher only learns about them by watching Intercom's markup.
  useEffect(() => watchPassiveNotifications(syncLauncherVisibility), []);

  useStartIntercomChecklistForNewAccounts(identifiedFor !== null);
}
